import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import { useLanguage } from "../contexts/LanguageContext";
import LanguageSelector from "./LanguageSelector";
import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;
const img = (u) => (!u ? undefined : /^https?:/.test(u) ? u : u.startsWith("/") ? BASE + u : BASE + "/api/files/" + u);

// Etichette tipologie (valori reali del DB usati da /search)
const TYPE_LABEL = {
  casa: { it: "Casa vacanza", en: "Holiday home", fr: "Maison de vacances", de: "Ferienhaus", es: "Casa de vacaciones" },
  appartamento: { it: "Appartamento", en: "Apartment", fr: "Appartement", de: "Apartment", es: "Apartamento" },
  bnb: { it: "B&B", en: "B&B", fr: "B&B", de: "B&B", es: "B&B" },
  agriturismo: { it: "Agriturismo", en: "Farm stay", fr: "Agritourisme", de: "Agriturismo", es: "Agroturismo" },
  locazione: { it: "Locazione turistica", en: "Short-term rental", fr: "Location touristique", de: "Ferienvermietung", es: "Alquiler turístico" },
  affittacamere: { it: "Affittacamere", en: "Guest rooms", fr: "Chambres d'hôtes", de: "Gästezimmer", es: "Habitaciones" },
  glamping: { it: "Glamping", en: "Glamping", fr: "Glamping", de: "Glamping", es: "Glamping" },
  villa: { it: "Villa", en: "Villa", fr: "Villa", de: "Villa", es: "Villa" },
  boutique: { it: "Boutique hotel", en: "Boutique hotel", fr: "Boutique hôtel", de: "Boutique-Hotel", es: "Boutique hotel" },
};

// Tipologie mostrate come link (valori reali di /search)
const TYPES = [
  ["casa", { it: "Case vacanza", en: "Holiday homes", fr: "Maisons de vacances", de: "Ferienhäuser", es: "Casas de vacaciones" }],
  ["bnb", { it: "B&B", en: "B&B", fr: "B&B", de: "B&B", es: "B&B" }],
  ["agriturismo", { it: "Agriturismo", en: "Farm stays", fr: "Agritourismes", de: "Agriturismi", es: "Agroturismos" }],
  ["glamping", { it: "Glamping", en: "Glamping", fr: "Glamping", de: "Glamping", es: "Glamping" }],
  ["affittacamere", { it: "Affittacamere", en: "Guest rooms", fr: "Chambres d'hôtes", de: "Gästezimmer", es: "Habitaciones" }],
  ["boutique", { it: "Boutique hotel", en: "Boutique hotels", fr: "Boutique hôtels", de: "Boutique-Hotels", es: "Boutique hotels" }],
];

const CARD_FALLBACK = ["#F6C9B6", "#BFE0CF", "#C9D7F2"];
const REGIONS = ["Liguria", "Toscana", "Sardegna", "Sicilia", "Puglia", "Lazio", "Lombardia", "Piemonte", "Veneto", "Campania"];

const T = {
  it: {
    pill_a: "strutture online ora", pill_b: " in Italia",
    h1a: "Prenota diretto.", h1b: "Zero commissioni.",
    lead: "Case vacanza, B&B, agriturismo e glamping in tutta Italia. Parli con l'host, prenoti al suo prezzo, nessun intermediario in mezzo.",
    nav_search: "Cerca alloggi", nav_site: "Sito", nav_wb: "Welcomebook", nav_park: "ParkHost", nav_prezzi: "Prezzi", nav_blog: "Blog", nav_pro: "Hostland Pro", nav_eventi: "Eventi",
    area_host: "Area Host", menu_open: "Apri il menu", menu_close: "Chiudi il menu",
    travelers: "VIAGGIATORI", hosts: "HOST", site_host: "Sito per host",
    pro_sub: "Per attività locali", eventi_sub: "Sagre, concerti, fiere",
    start_free: "Inizia gratis · 30 giorni",
    where: "DOVE", where_ph: "Città, regione o struttura", checkin: "CHECK-IN", checkout: "CHECK-OUT",
    guests: "OSPITI", guest1: "1 ospite", guestN: "ospiti", less: "Meno ospiti", more: "Più ospiti",
    search: "Cerca", types_h: "CHE TIPO DI ALLOGGIO?", rank: "Classifica host",
    tag_stay: "STRUTTURA", tag_region: "REGIONE", tag_city: "CITTÀ", featured: "Strutture in evidenza", commissions: "COMMISSIONI",
    host_kicker: "SEI UN HOST?", host_title: "Sito per case vacanze e B&B con Welcomebook digitale.",
    host_p: "Prenotazioni dirette a zero commissioni, guida ospiti in 5 lingue, QR code e invio ad Alloggiati Web e ISTAT. Tutto da un'unica dashboard.",
    park_cta: "ParkHost · €4,99/anno", price_from: "Prezzo diretto dall'host", contact: "Contatta l'host",
  },
  en: {
    pill_a: "properties online now", pill_b: " in Italy",
    h1a: "Book direct.", h1b: "Zero commission.",
    lead: "Holiday homes, B&Bs, farm stays and glamping all over Italy. Talk to the host, book at their price, no middleman.",
    nav_search: "Find stays", nav_site: "Website", nav_wb: "Welcomebook", nav_park: "ParkHost", nav_prezzi: "Pricing", nav_blog: "Blog", nav_pro: "Hostland Pro", nav_eventi: "Events",
    area_host: "Host area", menu_open: "Open menu", menu_close: "Close menu",
    travelers: "TRAVELERS", hosts: "HOSTS", site_host: "Website for hosts",
    pro_sub: "For local businesses", eventi_sub: "Festivals, concerts, fairs",
    start_free: "Start free · 30 days",
    where: "WHERE", where_ph: "City, region or property", checkin: "CHECK-IN", checkout: "CHECK-OUT",
    guests: "GUESTS", guest1: "1 guest", guestN: "guests", less: "Fewer guests", more: "More guests",
    search: "Search", types_h: "WHAT KIND OF STAY?", rank: "Host ranking",
    tag_stay: "STAY", tag_region: "REGION", tag_city: "CITY", featured: "Featured stays", commissions: "COMMISSION",
    host_kicker: "ARE YOU A HOST?", host_title: "Website for holiday homes & B&Bs with a digital Welcomebook.",
    host_p: "Direct commission-free bookings, 5-language guest guide, QR codes and automatic sending to Alloggiati Web and ISTAT. All from one dashboard.",
    park_cta: "ParkHost · €4.99/year", price_from: "Price direct from host", contact: "Contact the host",
  },
  fr: {
    pill_a: "hébergements en ligne", pill_b: " en Italie",
    h1a: "Réservez en direct.", h1b: "Zéro commission.",
    lead: "Maisons de vacances, B&B, agritourismes et glamping dans toute l'Italie. Parlez à l'hôte, réservez à son prix, sans intermédiaire.",
    nav_search: "Trouver un logement", nav_site: "Site", nav_wb: "Welcomebook", nav_park: "ParkHost", nav_prezzi: "Tarifs", nav_blog: "Blog", nav_pro: "Hostland Pro", nav_eventi: "Événements",
    area_host: "Espace hôte", menu_open: "Ouvrir le menu", menu_close: "Fermer le menu",
    travelers: "VOYAGEURS", hosts: "HÔTES", site_host: "Site pour hôtes",
    pro_sub: "Pour les entreprises locales", eventi_sub: "Fêtes, concerts, foires",
    start_free: "Commencez gratuitement · 30 jours",
    where: "OÙ", where_ph: "Ville, région ou logement", checkin: "ARRIVÉE", checkout: "DÉPART",
    guests: "VOYAGEURS", guest1: "1 voyageur", guestN: "voyageurs", less: "Moins de voyageurs", more: "Plus de voyageurs",
    search: "Rechercher", types_h: "QUEL TYPE DE LOGEMENT ?", rank: "Classement des hôtes",
    tag_stay: "LOGEMENT", tag_region: "RÉGION", tag_city: "VILLE", featured: "Hébergements en vedette", commissions: "COMMISSION",
    host_kicker: "VOUS ÊTES HÔTE ?", host_title: "Site web pour maisons de vacances et B&B avec Welcomebook numérique.",
    host_p: "Réservations directes sans commission, guide voyageur en 5 langues, QR codes et envoi automatique à Alloggiati Web et ISTAT. Le tout depuis un seul tableau de bord.",
    park_cta: "ParkHost · 4,99 €/an", price_from: "Prix direct de l'hôte", contact: "Contacter l'hôte",
  },
  de: {
    pill_a: "Unterkünfte online", pill_b: " in Italien",
    h1a: "Direkt buchen.", h1b: "Null Provision.",
    lead: "Ferienhäuser, B&Bs, Agriturismi und Glamping in ganz Italien. Sprechen Sie mit dem Gastgeber, buchen Sie zu seinem Preis, ohne Vermittler.",
    nav_search: "Unterkünfte finden", nav_site: "Website", nav_wb: "Welcomebook", nav_park: "ParkHost", nav_prezzi: "Preise", nav_blog: "Blog", nav_pro: "Hostland Pro", nav_eventi: "Events",
    area_host: "Gastgeber-Bereich", menu_open: "Menü öffnen", menu_close: "Menü schließen",
    travelers: "REISENDE", hosts: "GASTGEBER", site_host: "Website für Gastgeber",
    pro_sub: "Für lokale Unternehmen", eventi_sub: "Feste, Konzerte, Messen",
    start_free: "Kostenlos starten · 30 Tage",
    where: "WOHIN", where_ph: "Stadt, Region oder Unterkunft", checkin: "ANREISE", checkout: "ABREISE",
    guests: "GÄSTE", guest1: "1 Gast", guestN: "Gäste", less: "Weniger Gäste", more: "Mehr Gäste",
    search: "Suchen", types_h: "WELCHE ART VON UNTERKUNFT?", rank: "Gastgeber-Ranking",
    tag_stay: "UNTERKUNFT", tag_region: "REGION", tag_city: "STADT", featured: "Ausgewählte Unterkünfte", commissions: "PROVISION",
    host_kicker: "SIND SIE GASTGEBER?", host_title: "Website für Ferienhäuser & B&Bs mit digitalem Welcomebook.",
    host_p: "Direkte provisionsfreie Buchungen, Gästeführer in 5 Sprachen, QR-Codes und automatische Übermittlung an Alloggiati Web und ISTAT. Alles über ein einziges Dashboard.",
    park_cta: "ParkHost · 4,99 €/Jahr", price_from: "Preis direkt vom Gastgeber", contact: "Gastgeber kontaktieren",
  },
  es: {
    pill_a: "alojamientos online", pill_b: " en Italia",
    h1a: "Reserva directo.", h1b: "Cero comisiones.",
    lead: "Casas de vacaciones, B&B, agroturismos y glamping por toda Italia. Habla con el anfitrión, reserva a su precio, sin intermediarios.",
    nav_search: "Buscar alojamiento", nav_site: "Sitio", nav_wb: "Welcomebook", nav_park: "ParkHost", nav_prezzi: "Precios", nav_blog: "Blog", nav_pro: "Hostland Pro", nav_eventi: "Eventos",
    area_host: "Área anfitrión", menu_open: "Abrir el menú", menu_close: "Cerrar el menú",
    travelers: "VIAJEROS", hosts: "ANFITRIONES", site_host: "Sitio para anfitriones",
    pro_sub: "Para negocios locales", eventi_sub: "Fiestas, conciertos, ferias",
    start_free: "Empieza gratis · 30 días",
    where: "DÓNDE", where_ph: "Ciudad, región o alojamiento", checkin: "ENTRADA", checkout: "SALIDA",
    guests: "HUÉSPEDES", guest1: "1 huésped", guestN: "huéspedes", less: "Menos huéspedes", more: "Más huéspedes",
    search: "Buscar", types_h: "¿QUÉ TIPO DE ALOJAMIENTO?", rank: "Ranking de anfitriones",
    tag_stay: "ALOJAMIENTO", tag_region: "REGIÓN", tag_city: "CIUDAD", featured: "Alojamientos destacados", commissions: "COMISIÓN",
    host_kicker: "¿ERES ANFITRIÓN?", host_title: "Web para casas de vacaciones y B&B con Welcomebook digital.",
    host_p: "Reservas directas sin comisiones, guía para huéspedes en 5 idiomas, códigos QR y envío automático a Alloggiati Web e ISTAT. Todo desde un único panel.",
    park_cta: "ParkHost · 4,99 €/año", price_from: "Precio directo del anfitrión", contact: "Contactar al anfitrión",
  },
};

const CSS = `
.hh{--red:#E11D2E;--yellow:#FBBF24;--ink:#141414;--paper:#FBF7F0;--line:#EFE9DF;--muted:#5C5752;--body:#3D3935;--violet:#6D28D9;--display:'Bricolage Grotesque',system-ui,sans-serif;--text:'Inter',system-ui,sans-serif;background:var(--paper);color:var(--ink);font-family:var(--text);-webkit-font-smoothing:antialiased;position:relative}
.hh a{color:inherit;text-decoration:none}
.hh button{font:inherit;color:inherit;cursor:pointer}
.hh .wrap{max-width:1312px;margin:0 auto;padding:0 20px}
@media(min-width:1024px){.hh .wrap{padding:0 40px}}
.hh .hdr{padding-top:12px}
.hh .hdr-bar{display:flex;align-items:center;justify-content:space-between;gap:12px;height:64px;padding:0 8px 0 4px}
@media(min-width:1024px){.hh .hdr-bar{background:#fff;border-radius:22px;height:68px;padding:0 14px 0 20px;box-shadow:0 1px 0 rgba(20,20,20,.06)}}
.hh .logo{display:flex;align-items:center;gap:10px}
.hh .logo-mark{width:38px;height:38px;border-radius:10px;object-fit:cover;display:block}
.hh .logo-name{font:800 21px var(--display);letter-spacing:-.4px}
.hh .nav{display:none;align-items:center;gap:24px;font-size:15px;font-weight:500}
.hh .nav a:hover{color:var(--red)}
.hh .nav .on{color:var(--red);font-weight:700}
.hh .nav .strong{font-weight:700}
.hh .nav .violet{color:var(--violet);font-weight:700}
.hh .hdr-act{display:flex;align-items:center;gap:8px}
.hh .hdr-lang{display:none}
.hh .btn-dark{height:44px;padding:0 16px;border-radius:12px;background:var(--ink);color:#fff;display:inline-flex;align-items:center;font-weight:700;font-size:14px;border:none}
.hh .burger{width:44px;height:44px;border-radius:12px;border:1.5px solid #E6E0D6;background:#fff;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:5px;padding:0}
.hh .burger span{width:20px;height:2.5px;border-radius:2px;background:var(--ink)}
.hh .burger span:last-child{width:13px;margin-left:7px}
@media(min-width:1024px){.hh .nav,.hh .hdr-lang{display:flex}.hh .burger{display:none}.hh .btn-dark{padding:0 20px;font-size:15px}}
.hh-menu{position:fixed;inset:0;z-index:60;background:var(--ink);color:#fff;padding:0 16px env(safe-area-inset-bottom);display:flex;flex-direction:column;overflow-y:auto;transform:translateY(-8px);opacity:0;visibility:hidden;transition:opacity .2s,transform .2s,visibility .2s}
.hh-menu.open{opacity:1;transform:none;visibility:visible}
.hh-menu .logo-name{color:#fff}
.hh-menu .menu-top{height:76px;display:flex;align-items:center;justify-content:space-between}
.hh-menu .menu-close{width:44px;height:44px;border-radius:12px;border:1.5px solid #3A3633;background:transparent;display:flex;align-items:center;justify-content:center}
.hh-menu .menu-lbl{margin-top:8px;font-size:12px;font-weight:800;letter-spacing:2px;color:#C9C2B8}
.hh-menu .menu-big{display:flex;align-items:center;justify-content:space-between;padding:10px 0;font:800 36px/1.05 var(--display);color:var(--yellow)}
.hh-menu .menu-list a{display:block;padding:8px 0;font:800 30px/1.1 var(--display);color:#fff}
.hh-menu .menu-tiles{margin-top:16px;display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:8px}
.hh-menu .menu-tiles a{border-radius:16px;background:#26221F;padding:14px;display:flex;flex-direction:column;gap:4px;color:#fff}
.hh-menu .menu-tiles b{font-size:16px}
.hh-menu .menu-tiles small{font-size:13px;color:#C9C2B8}
.hh-menu .menu-foot{margin-top:auto;padding:24px 0;display:flex;flex-direction:column;gap:10px}
.hh-menu .menu-foot .row{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:10px}
.hh .cta-red{height:56px;border-radius:16px;background:var(--red);color:#fff;display:flex;align-items:center;justify-content:center;font-weight:800;font-size:17px;border:none;white-space:nowrap;padding:0 18px}
.hh-menu .cta-white{height:52px;border-radius:16px;background:#fff;color:var(--ink);display:flex;align-items:center;justify-content:center;font-weight:700}
.hh-menu .cta-ghost{height:52px;border-radius:16px;border:1.5px solid #3A3633;background:transparent;color:#fff;font-weight:700;display:flex;align-items:center;justify-content:center;padding:0}
@media(min-width:1024px){.hh-menu{display:none}}
.hh .hero{display:grid;gap:28px;padding-top:18px}
@media(min-width:1024px){.hh .hero{grid-template-columns:minmax(0,1fr) 470px;gap:40px;align-items:start;padding-top:54px}}
.hh .hero-copy{display:flex;flex-direction:column;gap:14px;position:relative}
@media(min-width:1024px){.hh .hero-copy{gap:22px}}
.hh .pill{align-self:flex-start;height:44px;padding:0 18px 0 14px;border-radius:999px;background:var(--ink);color:#fff;display:inline-flex;align-items:center;gap:10px;font-size:14px;font-weight:600}
.hh .pill .num{font:800 20px var(--display);font-variant-numeric:tabular-nums;min-width:38px}
.hh .live{width:10px;height:10px;border-radius:5px;background:#22C55E;animation:hhlivepulse 1.8s infinite}
@keyframes hhlivepulse{0%{box-shadow:0 0 0 0 rgba(34,197,94,.6)}70%{box-shadow:0 0 0 10px rgba(34,197,94,0)}100%{box-shadow:0 0 0 0 rgba(34,197,94,0)}}
.hh .pill .long{display:none}
@media(min-width:1024px){.hh .pill{height:46px;font-size:15px}.hh .pill .num{font-size:22px}.hh .pill .long{display:inline}}
.hh h1{margin:0;font:800 clamp(52px,14vw,104px)/.9 var(--display);letter-spacing:-.03em;max-width:12ch}
.hh h1 .accent{color:var(--red);position:relative;display:inline-block}
.hh h1 .accent svg{position:absolute;left:0;bottom:-.18em;width:100%;height:.26em;display:none}
@media(min-width:1024px){.hh h1 .accent svg{display:block}}
.hh .lead{margin:0;font-size:16px;line-height:1.5;color:var(--body);max-width:600px}
@media(min-width:1024px){.hh .lead{font-size:20px;margin-top:18px}}
.hh .sticker{position:absolute;right:0;top:56px;width:104px;height:104px;border-radius:50%;background:var(--yellow);border:3px solid #fff;display:flex;flex-direction:column;align-items:center;justify-content:center;transform:rotate(-12deg);box-shadow:0 10px 22px rgba(225,29,46,.25)}
.hh .sticker b{font:800 40px/.9 var(--display)}
.hh .sticker small{font-size:10px;font-weight:800;letter-spacing:1px}
@media(min-width:1024px){.hh .sticker{display:none}}
.hh .collage{display:none;position:relative;height:400px}
@media(min-width:1024px){.hh .collage{display:block}}
.hh .card{position:absolute;background:#fff;border-radius:22px;padding:10px;box-shadow:0 18px 40px rgba(20,20,20,.12);transition:transform .25s}
.hh .card:hover{transform:translateY(-4px) rotate(0deg)}
.hh .card img,.hh .card .ph{display:block;width:100%;border-radius:14px;object-fit:cover}
.hh .card .meta{padding:10px 6px 4px;display:flex;justify-content:space-between;align-items:center;gap:8px}
.hh .card b{display:block;font-size:15px}
.hh .card small{font-size:13px;color:var(--muted)}
.hh .c1{left:10px;top:40px;width:230px;transform:rotate(-6deg)}
.hh .c1 .ph,.hh .c1 img{height:170px}
.hh .c2{left:200px;top:0;width:250px;transform:rotate(4deg);box-shadow:0 22px 48px rgba(20,20,20,.16)}
.hh .c2 .ph,.hh .c2 img{height:190px}
.hh .c3{left:70px;top:250px;width:220px;transform:rotate(2deg)}
.hh .c3 .ph,.hh .c3 img{height:100px}
.hh .go{width:36px;height:36px;border-radius:50%;background:var(--ink);display:flex;align-items:center;justify-content:center;flex-shrink:0}
.hh .big-sticker{position:absolute;right:0;top:230px;width:150px;height:150px;border-radius:50%;background:var(--yellow);border:4px solid #fff;display:flex;flex-direction:column;align-items:center;justify-content:center;transform:rotate(-12deg);box-shadow:0 14px 30px rgba(225,29,46,.25)}
.hh .big-sticker b{font:800 58px/.9 var(--display)}
.hh .big-sticker small{font-size:13px;font-weight:800;letter-spacing:1.5px}
.hh .search{margin-top:20px;background:#fff;border-radius:26px;padding:8px;display:flex;flex-direction:column;gap:6px;box-shadow:0 16px 40px rgba(20,20,20,.12),0 0 0 1.5px var(--line);position:relative}
.hh .field{display:flex;flex-direction:column;gap:3px;padding:12px 14px;border-radius:18px;background:var(--paper);cursor:text}
.hh .field span{font-size:11px;font-weight:800;letter-spacing:1.5px}
.hh .field input{border:none;outline:none;background:transparent;padding:0;font:500 16px var(--text);color:var(--ink);width:100%;min-width:0}
.hh .field input::placeholder{color:#6B6560}
.hh .dates{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:6px}
.hh .guests{display:flex;align-items:center;justify-content:space-between;padding:8px 8px 8px 14px;border-radius:18px;background:var(--paper)}
.hh .guests .lbl{display:flex;flex-direction:column;gap:3px}
.hh .guests .lbl span{font-size:11px;font-weight:800;letter-spacing:1.5px}
.hh .guests output{font-size:16px;font-weight:600}
.hh .step{display:flex;gap:6px}
.hh .step button{width:44px;height:44px;border-radius:50%;border:1.5px solid #E6E0D6;background:#fff;font-size:20px;font-weight:700;display:flex;align-items:center;justify-content:center}
.hh .step button:disabled{opacity:.4;cursor:not-allowed}
.hh .go-search{height:58px;border-radius:18px;border:none;background:var(--red);color:#fff;display:flex;align-items:center;justify-content:center;gap:10px;font:800 18px var(--text)}
.hh .ac{position:absolute;z-index:20;left:8px;right:8px;top:calc(100% + 6px);background:#fff;border-radius:18px;box-shadow:0 18px 40px rgba(20,20,20,.18),0 0 0 1.5px var(--line);overflow:hidden;max-height:320px;overflow-y:auto}
@media(min-width:1024px){.hh .ac{left:14px;right:auto;width:420px;top:calc(100% + 10px)}}
.hh .ac button{width:100%;text-align:left;padding:12px 16px;display:flex;align-items:center;gap:10px;background:#fff;border:none;border-bottom:1px solid var(--line);font-size:15px}
.hh .ac button:last-child{border-bottom:none}
.hh .ac button:hover{background:var(--paper)}
.hh .ac .tag{margin-left:auto;font-size:11px;font-weight:800;letter-spacing:1px;color:var(--muted)}
@media(min-width:1024px){
.hh .search{margin-top:26px;flex-direction:row;align-items:center;border-radius:999px;padding:10px 10px 10px 14px;box-shadow:0 20px 50px rgba(20,20,20,.12),0 0 0 1.5px var(--line)}
.hh .field,.hh .guests{background:transparent;border-radius:0;padding:10px 26px;border-right:1.5px solid var(--line)}
.hh .field.where{flex:1.6}
.hh .dates{display:contents}
.hh .dates .field{flex:1}
.hh .guests{flex:1;border-right:none;padding:6px 18px 6px 26px}
.hh .field span,.hh .guests .lbl span{font-size:12px}
.hh .field input,.hh .guests output{font-size:17px}
.hh .go-search{height:68px;padding:0 34px;border-radius:999px;font-size:19px}
}
.hh .types-h{margin:20px 0 0;font-size:13px;font-weight:800;letter-spacing:1.5px}
.hh .types{margin-top:10px;display:flex;gap:8px;overflow-x:auto;scrollbar-width:none;padding-bottom:4px;margin-inline:-20px;padding-inline:20px}
.hh .types::-webkit-scrollbar{display:none}
.hh .types a{flex-shrink:0;height:44px;border-radius:999px;background:#fff;border:1.5px solid var(--line);padding:0 20px;display:flex;align-items:center;justify-content:center;font-size:14px;font-weight:700;white-space:nowrap}
.hh .types a:hover{border-color:var(--ink)}
.hh .below{display:flex;flex-direction:column;gap:14px;margin-top:4px}
.hh .cities{font-size:14px;color:var(--muted)}
.hh .cities a{font-weight:700;color:var(--ink)}
.hh .cities a:hover{color:var(--red)}
.hh .rank-btn{flex-shrink:0;align-self:stretch;width:100%;height:48px;border-radius:14px;background:var(--ink);color:#fff;display:inline-flex;align-items:center;justify-content:center;gap:10px;padding:0 18px;font-weight:700;font-size:15px}
.hh .rank-btn:hover{background:#000}
.hh .rank-btn .podium{display:flex;align-items:flex-end;gap:2px}
.hh .rank-btn .podium i{width:4px;border-radius:2px;background:var(--yellow);display:block}
@media(min-width:1024px){
.hh .types-h{display:none}
.hh .below{flex-direction:row;align-items:center;justify-content:space-between;margin-top:22px}
.hh .types{margin:0;padding:0;gap:10px;overflow:visible}
.hh .types a{height:48px;align-items:center;padding:0 24px;border-radius:999px;font-size:15px;font-weight:700}
.hh .types i{width:28px;height:28px;border-radius:8px}
.hh .rank-btn{width:auto;align-self:center}
}
.hh .host{margin:26px 0 0;background:var(--ink);color:#fff;border-radius:26px;padding:22px 20px;display:flex;flex-direction:column;gap:14px}
.hh .host .kicker{font-size:12px;font-weight:800;letter-spacing:2px;color:var(--yellow)}
.hh .host h2{margin:0;font:800 28px/1.05 var(--display)}
.hh .host p{margin:0;font-size:14px;line-height:1.5;color:#C9C2B8;max-width:620px}
.hh .host .ctas{display:flex;flex-direction:column;gap:10px}
.hh .host .cta-red{height:54px;font-size:16px}
.hh .cta-yellow{height:54px;border-radius:16px;border:2px solid var(--yellow);color:var(--yellow);display:flex;align-items:center;justify-content:center;font-weight:700;font-size:15px;padding:0 24px;background:transparent}
@media(min-width:1024px){
.hh .host{margin-top:34px;border-radius:30px;padding:30px 40px;flex-direction:row;align-items:center;justify-content:space-between;gap:30px}
.hh .host h2{font-size:34px;letter-spacing:-.5px}
.hh .host p{font-size:15px;margin-top:6px}
.hh .host .ctas{flex-direction:row}
.hh .host .cta-red{height:58px;padding:0 26px;font-size:17px}
.hh .cta-yellow{height:58px}
}
@media(prefers-reduced-motion:reduce){.hh .live{animation:none}.hh-menu{transition:none}}
.hh .sr-only{position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap}
`;

const ArrowIcon = ({ stroke = "#fff" }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
);

export default function HomeHero() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { lang } = useLanguage();
  const tr = T[lang] || T.it;

  const [count, setCount] = useState(0);
  const [target, setTarget] = useState(0);
  const [props, setProps] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);

  // form state
  const [q, setQ] = useState("");
  const [checkin, setCheckin] = useState("");
  const [checkout, setCheckout] = useState("");
  const [guests, setGuests] = useState(2);
  const [sugg, setSugg] = useState([]);
  const [suggOpen, setSuggOpen] = useState(false);
  const suggTmr = useRef(null);
  const menuRef = useRef(null);
  const burgerRef = useRef(null);

  const today = new Date().toISOString().slice(0, 10);
  const nextDay = (d) => { const x = new Date(d); x.setDate(x.getDate() + 1); return x.toISOString().slice(0, 10); };

  useEffect(() => {
    axios.get(`${API}/leaderboard/stats`).then((r) => setTarget(r.data.total_properties || 0)).catch(() => {});
    axios.get(`${API}/search/properties?guests=1&limit=200`).then((r) => {
      setProps(r.data.results || []);
    }).catch(() => {});
  }, []);

  // Count-up animation 0 -> target in ~1.4s (respects reduced motion)
  useEffect(() => {
    if (!target) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) { setCount(target); return; }
    let raf, start = null; const dur = 1400;
    const tick = (ts) => { if (!start) start = ts; const t = Math.min(1, (ts - start) / dur); const e = 1 - Math.pow(1 - t, 3); setCount(Math.round(target * e)); if (t < 1) raf = requestAnimationFrame(tick); };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target]);

  // Mobile menu: scroll lock + Esc + focus trap
  const closeMenu = useCallback(() => { setMenuOpen(false); }, []);
  useEffect(() => {
    if (!menuOpen) { document.body.style.overflow = ""; return; }
    document.body.style.overflow = "hidden";
    const first = menuRef.current?.querySelector("button,a");
    first?.focus();
    const onKey = (e) => {
      if (e.key === "Escape") { closeMenu(); burgerRef.current?.focus(); }
      if (e.key === "Tab" && menuRef.current) {
        const f = menuRef.current.querySelectorAll("a,button");
        if (!f.length) return;
        const first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener("keydown", onKey);
    const mq = window.matchMedia("(min-width:1024px)");
    const onMq = (m) => { if (m.matches) closeMenu(); };
    mq.addEventListener("change", onMq);
    return () => { document.body.style.overflow = ""; document.removeEventListener("keydown", onKey); mq.removeEventListener("change", onMq); };
  }, [menuOpen, closeMenu]);

  const onWhere = (v) => {
    setQ(v);
    if (suggTmr.current) clearTimeout(suggTmr.current);
    if (v.trim().length < 2) { setSugg([]); setSuggOpen(false); return; }
    const needle = v.trim().toLowerCase();
    const localProps = props.filter((p) => (p.name || "").toLowerCase().includes(needle)).slice(0, 3).map((p) => ({ label: p.name, kind: "property", pid: p.property_id }));
    const localRegions = REGIONS.filter((r) => r.toLowerCase().includes(needle)).slice(0, 2).map((r) => ({ label: r, kind: "region" }));
    suggTmr.current = setTimeout(async () => {
      let comuni = [];
      try { const r = await axios.get(`${API}/comuni/search?q=${encodeURIComponent(v)}`); comuni = (r.data.results || []).slice(0, 5).map((c) => ({ label: c.nome, kind: "city", sub: c.provincia })); } catch { /* keep local */ }
      setSugg([...localProps, ...localRegions, ...comuni].slice(0, 7));
      setSuggOpen(true);
    }, 180);
  };

  const pickSugg = (s) => {
    setSuggOpen(false);
    if (s.kind === "property" && s.pid) { navigate(`/property/${s.pid}`); return; }
    setQ(s.label);
  };

  const onCheckin = (v) => {
    setCheckin(v);
    if (v && (!checkout || checkout <= v)) setCheckout(nextDay(v));
  };

  const submit = (e) => {
    e.preventDefault();
    const p = new URLSearchParams();
    if (q.trim()) p.set("q", q.trim());
    if (checkin) p.set("checkin", checkin);
    if (checkout) p.set("checkout", checkout);
    p.set("guests", String(guests));
    navigate(`/prenotasenzacommissioni?${p.toString()}`);
  };

  const go = (href) => (e) => { if (href.startsWith("/") && !href.startsWith("//")) { e.preventDefault(); closeMenu(); navigate(href); } };
  // In-page anchors: when the section exists on the current page, smooth-scroll
  // to it instead of letting the browser jump (which the mount scroll-to-top
  // would otherwise override). On other routes, fall back to default navigation.
  const onAnchor = (id) => (e) => {
    closeMenu();
    const el = document.getElementById(id);
    if (el) {
      e.preventDefault();
      window.history.replaceState(null, "", `/#${id}`);
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };
  const areaHost = user ? "/dashboard" : "/login";
  const regHref = user ? "/dashboard" : "/login?mode=signup&as=host";
  const parkHref = user ? "/parkhost/enroll" : "/login?mode=signup&as=parkhost";

  // Collage: top 3 published properties by views (clicks)
  const collage = [...props].sort((a, b) => (b.clicks_count || 0) - (a.clicks_count || 0)).slice(0, 3);
  while (collage.length < 3 && props.length) collage.push(props[collage.length % props.length]);

  const Logo = ({ dark }) => (
    <>
      <img src="/hostland-logo.png" alt="Logo Hostland" className="logo-mark" />
      <span className="logo-name" style={dark ? { color: "#fff" } : undefined}>Hostland</span>
    </>
  );

  return (
    <div className="hh" data-testid="home-hero">
      <style>{CSS}</style>

      {/* HEADER */}
      <header className="hdr">
        <div className="wrap">
          <div className="hdr-bar">
            <a href="/" className="logo" aria-label="Hostland, home" onClick={go("/")} data-testid="hero-logo"><Logo /></a>
            <nav className="nav" aria-label="Principale">
              <a href="/prenotasenzacommissioni" className="on" onClick={go("/prenotasenzacommissioni")} data-testid="nav-search">{tr.nav_search}</a>
              <a href="/#video-sito" onClick={onAnchor("video-sito")} data-testid="nav-site">{tr.nav_site}</a>
              <a href="/#video-welcomebook" onClick={onAnchor("video-welcomebook")} data-testid="nav-welcomebook">{tr.nav_wb}</a>
              <a href="/#parkhost" onClick={onAnchor("parkhost")} data-testid="nav-parkhost">{tr.nav_park}</a>
              <a href="/#prezzi" onClick={onAnchor("prezzi")} data-testid="nav-prezzi">{tr.nav_prezzi}</a>
              <a href="/blog" onClick={go("/blog")} data-testid="nav-blog">{tr.nav_blog}</a>
              <a href="/pro" target="_blank" rel="noopener noreferrer" className="strong" data-testid="nav-pro">{tr.nav_pro}</a>
              <a href="/eventi" target="_blank" rel="noopener noreferrer" className="violet" data-testid="nav-eventi">{tr.nav_eventi}</a>
            </nav>
            <div className="hdr-act">
              <span className="hdr-lang"><LanguageSelector variant="compact" /></span>
              <a href={areaHost} className="btn-dark" onClick={go(areaHost)} data-testid="nav-area-host">{tr.area_host}</a>
              <button className="burger" type="button" aria-label={tr.menu_open} aria-expanded={menuOpen} aria-controls="hh-menu" ref={burgerRef} onClick={() => setMenuOpen(true)} data-testid="mobile-menu-toggle">
                <span></span><span></span><span></span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* MOBILE MENU */}
      <div className={`hh-menu${menuOpen ? " open" : ""}`} id="hh-menu" role="dialog" aria-modal="true" aria-label="Menu" aria-hidden={!menuOpen} ref={menuRef} data-testid="mobile-menu">
        <div className="wrap" style={{ display: "flex", flexDirection: "column", flex: 1 }}>
          <div className="menu-top">
            <span className="logo"><Logo dark /></span>
            <button className="menu-close" type="button" aria-label={tr.menu_close} onClick={() => { closeMenu(); burgerRef.current?.focus(); }} data-testid="mobile-menu-close">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
            </button>
          </div>
          <div className="menu-lbl">{tr.travelers}</div>
          <a href="/prenotasenzacommissioni" className="menu-big" onClick={go("/prenotasenzacommissioni")} data-testid="mobile-nav-search">{tr.nav_search}
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#FBBF24" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
          </a>
          <div className="menu-lbl" style={{ marginTop: 14 }}>{tr.hosts}</div>
          <nav className="menu-list" aria-label="Menu host">
            <a href="/#video-sito" onClick={onAnchor("video-sito")} data-testid="mobile-nav-site">{tr.site_host}</a>
            <a href="/#video-welcomebook" onClick={onAnchor("video-welcomebook")} data-testid="mobile-nav-welcomebook">{tr.nav_wb}</a>
            <a href="/#parkhost" onClick={onAnchor("parkhost")} data-testid="mobile-nav-parkhost">{tr.nav_park}</a>
            <a href="/#prezzi" onClick={onAnchor("prezzi")} data-testid="mobile-nav-prezzi">{tr.nav_prezzi}</a>
            <a href="/blog" onClick={go("/blog")} data-testid="mobile-nav-blog">{tr.nav_blog}</a>
          </nav>
          <div className="menu-tiles">
            <a href="/pro" target="_blank" rel="noopener noreferrer" onClick={() => closeMenu()} data-testid="mobile-nav-pro"><b>Hostland Pro</b><small>{tr.pro_sub}</small></a>
            <a href="/eventi" target="_blank" rel="noopener noreferrer" onClick={() => closeMenu()} data-testid="mobile-nav-eventi"><b style={{ color: "#C4B5FD" }}>Hostland Eventi</b><small>{tr.eventi_sub}</small></a>
          </div>
          <div className="menu-foot">
            <a href={regHref} className="cta-red" onClick={go(regHref)} data-testid="mobile-cta-register">{tr.start_free}</a>
            <div className="row">
              <a href={areaHost} className="cta-white" onClick={go(areaHost)} data-testid="mobile-cta-area-host">{tr.area_host}</a>
              <span className="cta-ghost"><LanguageSelector variant="compact" /></span>
            </div>
          </div>
        </div>
      </div>

      {/* HERO */}
      <main className="wrap">
        <section className="hero">
          <div className="hero-copy">
            <a href="/prenotasenzacommissioni" className="pill" onClick={go("/prenotasenzacommissioni")} data-testid="hero-count-pill">
              <span className="live" aria-hidden="true"></span>
              <span className="num" data-testid="hero-count">{count}</span>
              <span>{tr.pill_a}<span className="long">{tr.pill_b}</span></span>
            </a>
            <span className="sticker" aria-hidden="true"><b>0%</b><small>{tr.commissions}</small></span>
            <h1 data-testid="hero-h1">{tr.h1a}<br /><span className="accent">{tr.h1b}
              <svg viewBox="0 0 640 26" preserveAspectRatio="none" fill="none" aria-hidden="true"><path d="M6 18C160 4 420 4 634 14" stroke="#FBBF24" strokeWidth="10" strokeLinecap="round" /></svg>
            </span></h1>
            <p className="lead">{tr.lead}</p>
          </div>

          <div className="collage" aria-label={tr.featured}>
            {collage.map((p, i) => {
              const label = TYPE_LABEL[p.property_type]?.[lang] || TYPE_LABEL[p.property_type]?.it || (lang === "it" ? "Struttura" : "Property");
              const cls = ["c1", "c2", "c3"][i];
              const cover = img(p.cover);
              return (
                <a key={p.property_id + i} href={`/property/${p.property_id}`} className={`card ${cls}`} onClick={go(`/property/${p.property_id}`)} data-testid={`hero-collage-${i}`}>
                  {cover
                    ? <img src={cover} alt={`${label} a ${p.comune_name || "Italia"}`} width="230" height={[170, 190, 100][i]} loading={i === 0 ? "eager" : "lazy"} />
                    : <span className="ph" style={{ background: CARD_FALLBACK[i], height: [170, 190, 100][i] }} />}
                  <span className="meta">
                    <span><b>{label}</b><small>{i === 1 ? tr.contact : tr.price_from}</small></span>
                    {i === 1 && <span className="go"><ArrowIcon /></span>}
                  </span>
                </a>
              );
            })}
            <span className="big-sticker" aria-hidden="true"><b>0%</b><small>{tr.commissions}</small></span>
          </div>
        </section>

        {/* RICERCA */}
        <form className="search" action="/prenotasenzacommissioni" method="get" role="search" aria-label={tr.nav_search} onSubmit={submit} data-testid="hero-search-form">
          <label className="field where">
            <span>{tr.where}</span>
            <input name="q" type="search" value={q} onChange={(e) => onWhere(e.target.value)} onFocus={() => { if (sugg.length) setSuggOpen(true); }} onBlur={() => setTimeout(() => setSuggOpen(false), 150)} placeholder={tr.where_ph} autoComplete="off" enterKeyHint="search" data-testid="hero-where-input" />
          </label>
          {suggOpen && sugg.length > 0 && (
            <div className="ac" data-testid="hero-autocomplete">
              {sugg.map((s, i) => (
                <button type="button" key={s.kind + s.label + i} onMouseDown={(e) => e.preventDefault()} onClick={() => pickSugg(s)} data-testid={`hero-sugg-${i}`}>
                  {s.label}{s.sub ? <span style={{ color: "var(--muted)" }}>&nbsp;({s.sub})</span> : null}
                  <span className="tag">{s.kind === "property" ? tr.tag_stay : s.kind === "region" ? tr.tag_region : tr.tag_city}</span>
                </button>
              ))}
            </div>
          )}
          <div className="dates">
            <label className="field">
              <span>{tr.checkin}</span>
              <input name="checkin" type="date" value={checkin} min={today} onChange={(e) => onCheckin(e.target.value)} aria-label={tr.checkin} data-testid="hero-checkin-input" />
            </label>
            <label className="field">
              <span>{tr.checkout}</span>
              <input name="checkout" type="date" value={checkout} min={checkin ? nextDay(checkin) : today} onChange={(e) => setCheckout(e.target.value)} aria-label={tr.checkout} data-testid="hero-checkout-input" />
            </label>
          </div>
          <div className="guests">
            <div className="lbl"><span>{tr.guests}</span><output aria-live="polite" data-testid="hero-guests-out">{guests === 1 ? tr.guest1 : `${guests} ${tr.guestN}`}</output></div>
            <div className="step">
              <button type="button" aria-label={tr.less} disabled={guests <= 1} onClick={() => setGuests((g) => Math.max(1, g - 1))} data-testid="hero-guests-minus">−</button>
              <button type="button" aria-label={tr.more} disabled={guests >= 16} onClick={() => setGuests((g) => Math.min(16, g + 1))} data-testid="hero-guests-plus">+</button>
            </div>
          </div>
          <button type="submit" className="go-search" data-testid="hero-search-submit">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></svg>
            {tr.search}
          </button>
        </form>

        {/* TIPOLOGIE + METE */}
        <p className="types-h">{tr.types_h}</p>
        <div className="below">
          <nav className="types" aria-label="Tipologie di alloggio">
            {TYPES.map(([slug, label]) => (
              <a key={slug} href={`/search?type=${slug}&guests=2`} onClick={go(`/search?type=${slug}&guests=2`)} data-testid={`hero-type-${slug}`}>{label[lang] || label.it}</a>
            ))}
          </nav>
          <a href="/leaderboard" className="rank-btn" onClick={go("/leaderboard")} data-testid="hero-rank-btn">
            <span className="podium" aria-hidden="true"><i style={{ height: 12 }} /><i style={{ height: 18 }} /><i style={{ height: 9 }} /></span>
            {tr.rank}
            <ArrowIcon />
          </a>
        </div>

        {/* FASCIA HOST */}
        <section className="host" aria-labelledby="hostTitle">
          <div>
            <div className="kicker">{tr.host_kicker}</div>
            <h2 id="hostTitle">{tr.host_title}</h2>
            <p>{tr.host_p}</p>
          </div>
          <div className="ctas">
            <a href={regHref} className="cta-red" onClick={go(regHref)} data-testid="host-cta-register">{tr.start_free}</a>
            <a href={parkHref} className="cta-yellow" onClick={go(parkHref)} data-testid="host-cta-parkhost">{tr.park_cta}</a>
          </div>
        </section>
      </main>
    </div>
  );
}
